import React, { memo } from "react";
import { Button } from "@/shared/components/ui/button";
import { SlidersHorizontal } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Switch } from "@/shared/components/ui/switch";
import { type FilterableAppId, FILTERABLE_APPLICATIONS } from "../../hooks/useApplicationFilter";
import styles from "./HeaderBar.module.css";

// App icons
import outlookIcon from "@/assets/applications/OutlookIcon.svg";
import wordIcon from "@/assets/applications/WordIcon.svg";
import excelIcon from "@/assets/applications/ExcelIcon.svg";
import powerpointIcon from "@/assets/applications/PowerpointIcon.svg";
import adobeIcon from "@/assets/applications/AdobeIcon.svg";
import kofaxIcon from "@/assets/applications/KofaxIcon.svg";
import foxitIcon from "@/assets/applications/FoxitIcon.svg";
import zoomIcon from "@/assets/applications/ZoomIcon.svg";
import teamsIcon from "@/assets/applications/TeamsIcon.svg";

// Map app IDs to their icons
const APP_ICONS: Record<FilterableAppId, string> = {
  outlook: outlookIcon,
  word: wordIcon,
  excel: excelIcon,
  powerpoint: powerpointIcon,
  adobe: adobeIcon,
  kofax: kofaxIcon,
  foxit: foxitIcon,
  zoom: zoomIcon,
  teams: teamsIcon,
};

export interface ApplicationFilterProps {
  /** Check if a specific app is currently visible */
  isVisible: (appId: FilterableAppId) => boolean;
  /** Toggle visibility of a specific app */
  onToggle: (appId: FilterableAppId) => void;
  /** Whether any filter is currently active */
  isFilterActive: boolean;
  /** Show all applications */
  onShowAll: () => void;
  /** Hide all applications */
  onHideAll: () => void;
}

export const ApplicationFilter: React.FC<ApplicationFilterProps> = memo(({
  isVisible,
  onToggle,
  isFilterActive,
  onShowAll,
  onHideAll,
}) => {
  return (
    <div className={styles.filterButton}>
      <Popover>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className={`${styles.actionButtonIcon} border-border text-foreground bg-card hover:bg-secondary relative`}
              >
                <SlidersHorizontal className="h-4 w-4" />
                {isFilterActive && (
                  <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-blue-500 border-2 border-card" />
                )}
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>Filter Apps</p>
          </TooltipContent>
        </Tooltip>
        <PopoverContent className="w-64 p-0" align="start">
          <div className="p-3 border-b border-border">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-sm text-foreground">Filter by Application</h4>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onShowAll}
                  className="text-xs text-blue-500 hover:text-blue-600 hover:underline"
                >
                  All
                </button>
                <span className="text-xs text-muted-foreground">/</span>
                <button
                  type="button"
                  onClick={onHideAll}
                  className="text-xs text-blue-500 hover:text-blue-600 hover:underline"
                >
                  None
                </button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Show/hide entries by source app</p>
          </div>
          <div className="p-2">
            {FILTERABLE_APPLICATIONS.map((app) => (
              <div
                key={app.id}
                className="flex items-center justify-between px-2 py-1.5 rounded-md hover:bg-secondary"
              >
                <div className="flex items-center gap-3">
                  <img
                    src={APP_ICONS[app.id]}
                    alt={app.displayName}
                    className="w-5 h-5"
                  />
                  <span className="text-sm text-foreground">{app.displayName}</span>
                </div>
                <Switch
                  checked={isVisible(app.id)}
                  onCheckedChange={() => onToggle(app.id)}
                  className="scale-75"
                />
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
});

export default ApplicationFilter;
